import { numFont, plateFont } from '../../utils/fonts';
import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  Divider,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CheckIcon from '@mui/icons-material/Check';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useUserStore } from '../../store';
import { formatDate } from '../../utils';
import { formatPriceWithCurrency } from '../../utils/vat';
import { partsSaleErrorMessage, serverLineDisplay } from './salesUtils';

function Meta({ label, value, last }) {
  return (
    <Box
      sx={{
        px: 2.5,
        py: 1.75,
        // Side-by-side on sm+, stacked on phones — borders follow the layout.
        borderRight: { xs: 'none', sm: last ? 'none' : '1px solid' },
        borderBottom: { xs: last ? 'none' : '1px solid', sm: 'none' },
        borderColor: 'divider',
        minWidth: 0,
      }}
    >
      <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 600, letterSpacing: '0.06em', display: 'block', lineHeight: 1.6 }}>
        {label}
      </Typography>
      <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>
        {value}
      </Typography>
    </Box>
  );
}

function TotalRow({ label, value, muted, big }) {
  return (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', py: 0.375 }}>
      <Typography sx={{ fontSize: big ? 15 : 13.5, fontWeight: big ? 700 : 400 }} color={muted ? 'text.secondary' : 'text.primary'}>
        {label}
      </Typography>
      <Typography sx={{ ...numFont, fontWeight: big ? 700 : 500, fontSize: big ? 19 : 13.5 }} color={muted ? 'text.secondary' : 'text.primary'}>
        {value}
      </Typography>
    </Box>
  );
}

/**
 * Koontilasku preview + confirm: consolidate the selected open lähetteet into
 * one draft invoice. Fetches each sale's detail for a real line-level preview
 * (source note + date preserved per line, exactly like the invoice will be).
 */
function KoontilaskuDialog({ open, onClose, saleIds, onDone }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { currentOrganization } = useUserStore();
  const orgDefaultVat = currentOrganization?.default_price_includes_vat ?? false;
  const vatSuffix = (inclVat) => t(inclVat ? 'incl. VAT' : 'excl. VAT');
  const [sales, setSales] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [stage, setStage] = useState('preview'); // preview | done
  const [invoice, setInvoice] = useState(null);
  const [error, setError] = useState('');

  // Key the fetch on the ids' CONTENT — the parent passes a fresh array each
  // render and an identity dependency would refetch in a loop.
  const idsKey = JSON.stringify(saleIds || []);
  useEffect(() => {
    if (!open) return;
    setStage('preview');
    setInvoice(null);
    setError('');
    setSales([]);
    const ids = JSON.parse(idsKey);
    if (ids.length === 0) return;
    setLoading(true);
    Promise.all(ids.map((sid) => axios.get(`/api/parts-sales/${sid}/`)))
      .then((resps) => setSales(resps.map((r) => r.data).sort((a, b) => a.number - b.number)))
      .catch(() => setError(t('parts_sales.loadFailed')))
      .finally(() => setLoading(false));
  }, [open, idsKey, t]);

  const submit = async () => {
    setSubmitting(true);
    setError('');
    try {
      const resp = await axios.post('/api/parts-sales/consolidate/', { sale_ids: saleIds });
      setInvoice(resp.data.invoice);
      setStage('done');
    } catch (e) {
      setError(partsSaleErrorMessage(e, t, 'parts_sales.consolidateFailed'));
    } finally {
      setSubmitting(false);
    }
  };

  const customer = sales[0] || {};
  const rows = [];
  sales.forEach((s) => {
    (s.lines || [])
      .filter((l) => l.status === 'active')
      .forEach((l) => rows.push({ ...l, srcNumber: s.number, srcDate: s.created_at }));
  });
  const net = sales.reduce((a, s) => a + parseFloat(s.total_standard_excl_vat || 0), 0);
  const vat = sales.reduce((a, s) => a + parseFloat(s.total_standard_vat || 0), 0);
  const marginGross = sales.reduce((a, s) => a + parseFloat(s.total_margin_incl_vat || 0), 0);
  const total = sales.reduce((a, s) => a + parseFloat(s.total_incl_vat || 0), 0);
  const dates = sales.map((s) => s.created_at).sort();

  return (
    <Dialog open={open} onClose={submitting ? undefined : onClose} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: '16px' } }}>
      {stage === 'preview' ? (
        <Box>
          {/* Header */}
          <Box sx={{ px: 3, py: 2.25, borderBottom: '1px solid', borderColor: 'divider', display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
            <Box>
              <Typography sx={{ fontSize: 19, fontWeight: 700 }}>{t('parts_sales.koontilasku')}</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                {t('parts_sales.consolidateSummary', { count: sales.length })} · {customer.customer_name || ''}
              </Typography>
            </Box>
            <IconButton size="small" onClick={onClose} disabled={submitting}><CloseIcon /></IconButton>
          </Box>

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              {/* Meta strip */}
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' }, borderBottom: '1px solid', borderColor: 'divider' }}>
                <Meta label={t('parts_sales.customer')} value={customer.customer_name || '—'} />
                <Meta
                  label={t('parts_sales.dateRange')}
                  value={dates.length ? `${formatDate(dates[0])} – ${formatDate(dates[dates.length - 1])}` : '—'}
                />
                <Meta label={t('parts_sales.paymentTerms')} value={`${customer.payment_terms_days ?? '—'} ${t('parts_sales.daysNet')}`} last />
              </Box>

              {/* Line preview — wide table scrolls inside its own container on phones */}
              <Box sx={{ maxHeight: 320, overflowY: 'auto', overflowX: 'auto' }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ bgcolor: 'grey.50' }}>{t('parts_sales.sourceCol')}</TableCell>
                      <TableCell sx={{ bgcolor: 'grey.50' }}>{t('parts_sales.descriptionCol')}</TableCell>
                      <TableCell align="right" sx={{ bgcolor: 'grey.50' }}>{t('parts_sales.qty')}</TableCell>
                      <TableCell align="right" sx={{ bgcolor: 'grey.50', whiteSpace: 'nowrap' }}>
                        {t('parts_sales.unitPriceShort')}{' '}
                        <Typography component="span" variant="caption" color="text.disabled">
                          {vatSuffix(orgDefaultVat)}
                        </Typography>
                      </TableCell>
                      <TableCell align="right" sx={{ bgcolor: 'grey.50', whiteSpace: 'nowrap' }}>
                        {orgDefaultVat ? `${t('parts_sales.total')} ` : `${t('parts_sales.netCol')} `}
                        <Typography component="span" variant="caption" color="text.disabled">
                          {vatSuffix(orgDefaultVat)}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {rows.map((l, i) => (
                      <TableRow key={`${l.srcNumber}-${l.id}-${i}`}>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>
                          <Typography variant="caption" sx={{ ...numFont, fontWeight: 600 }}>#{l.srcNumber}</Typography>
                          <Typography variant="caption" color="text.disabled" sx={{ display: 'block', ...numFont }}>
                            {formatDate(l.srcDate)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                            <Typography variant="body2">{l.part_name || l.description}</Typography>
                            {l.is_margin_vat_scheme && (
                              <Chip size="small" variant="outlined" color="warning" label={t('parts_sales.marginTag')} sx={{ height: 18, fontSize: 10 }} />
                            )}
                            {!l.part_id && (
                              <Chip size="small" variant="outlined" label={t('parts_sales.freeTag')} sx={{ height: 18, fontSize: 10 }} />
                            )}
                          </Box>
                          {l.target_label && (
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', ...plateFont }} data-testid="koontilasku-line-target">
                              {l.target_label}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell align="right" sx={{ ...numFont }}>{parseFloat(l.quantity)}</TableCell>
                        <TableCell align="right" sx={{ ...numFont, whiteSpace: 'nowrap' }}>
                          {(() => {
                            const d = serverLineDisplay(l, orgDefaultVat);
                            return (
                              <>
                                {formatPriceWithCurrency(d.unit)}
                                {d.inclVat !== orgDefaultVat && (
                                  <Typography component="span" variant="caption" color="text.disabled" sx={{ ml: 0.5 }}>
                                    {vatSuffix(d.inclVat)}
                                  </Typography>
                                )}
                              </>
                            );
                          })()}
                        </TableCell>
                        <TableCell align="right" sx={{ ...numFont, fontWeight: 600, whiteSpace: 'nowrap' }}>
                          {formatPriceWithCurrency(serverLineDisplay(l, orgDefaultVat).total)}
                        </TableCell>
                      </TableRow>
                    ))}
                    {rows.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={5}>
                          <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
                            {t('parts_sales.noLines')}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </Box>

              {/* Undelivered special-order lines still get billed — make that loud. */}
              {rows.some((l) => !l.handed_over_at) && (
                <Alert severity="warning" sx={{ mx: 3, mt: 2 }} data-testid="koontilasku-undelivered-warning">
                  {t('parts_sales.undeliveredWarning')}
                </Alert>
              )}

              {/* Footer: note + totals + action */}
              <Box sx={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 3, px: 3, py: 2.25, borderTop: '1px solid', borderColor: 'divider', bgcolor: 'grey.50', flexWrap: 'wrap' }}>
                <Box sx={{ display: 'flex', gap: 1, maxWidth: 340 }}>
                  <InfoOutlinedIcon sx={{ fontSize: 16, color: 'text.disabled', mt: 0.25, flexShrink: 0 }} />
                  <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.45 }}>
                    {t('parts_sales.koontilaskuDraftNote')}
                  </Typography>
                </Box>
                <Box sx={{ minWidth: 240 }}>
                  <TotalRow label={t('parts_sales.netTotal')} value={formatPriceWithCurrency(net)} />
                  {marginGross > 0 && (
                    <TotalRow label={t('parts_sales.marginLinesInclVat')} value={formatPriceWithCurrency(marginGross)} muted />
                  )}
                  <TotalRow label={t('parts_sales.vatAmount')} value={formatPriceWithCurrency(vat)} muted />
                  <Divider sx={{ my: 1 }} />
                  <TotalRow label={t('parts_sales.total')} value={formatPriceWithCurrency(total)} big />
                  {error && <Alert severity="error" sx={{ mt: 1.5 }}>{error}</Alert>}
                  <Button
                    variant="contained"
                    color="secondary"
                    size="large"
                    fullWidth
                    startIcon={submitting ? <CircularProgress size={18} color="inherit" /> : <ReceiptLongIcon />}
                    disabled={submitting || loading || rows.length === 0}
                    onClick={submit}
                    data-testid="koontilasku-confirm"
                    sx={{ mt: 1.75, borderRadius: '10px' }}
                  >
                    {t('parts_sales.createKoontilasku')}
                  </Button>
                </Box>
              </Box>
            </>
          )}
        </Box>
      ) : (
        /* DONE */
        <Box sx={{ px: 4, pt: 4.5, pb: 3.5, textAlign: 'center' }}>
          <Box sx={{ width: 76, height: 76, borderRadius: '50%', bgcolor: '#e6ffed', display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto', mb: 2 }}>
            <CheckIcon sx={{ fontSize: 42, color: '#2f8c54' }} />
          </Box>
          <Typography sx={{ fontSize: 22, fontWeight: 700 }}>{t('parts_sales.koontilaskuCreatedTitle')}</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
            {t('parts_sales.invoiceLabel')}{' '}
            <Box component="span" sx={{ ...numFont, fontWeight: 600 }}>{invoice?.invoice_number}</Box>
            {' · '}
            {formatPriceWithCurrency(parseFloat(invoice?.total_incl_vat || 0))}
            {' · '}
            {t('parts_sales.notesMarkedInvoiced', { count: sales.length })}
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, justifyContent: 'center', mt: 1.75 }}>
            {sales.map((s) => (
              <Chip
                key={s.id}
                size="small"
                icon={<TaskAltIcon sx={{ fontSize: 14 }} />}
                label={`#${s.number}`}
                sx={{ bgcolor: '#e6ffed', color: '#2f8c54', fontWeight: 600, ...numFont }}
              />
            ))}
          </Box>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)', gap: 1, mt: 3 }}>
            <Button
              variant="outlined"
              startIcon={<ReceiptLongIcon />}
              onClick={() => invoice?.id && navigate(`/invoices/${invoice.id}`)}
              data-testid="koontilasku-open-invoice"
              sx={{ borderRadius: '10px' }}
            >
              {t('parts_sales.openInvoice')}
            </Button>
            <Button
              variant="contained"
              color="secondary"
              startIcon={<CheckIcon />}
              onClick={() => onDone(invoice)}
              data-testid="koontilasku-done"
              sx={{ borderRadius: '10px' }}
            >
              {t('parts_sales.done')}
            </Button>
          </Box>
        </Box>
      )}
    </Dialog>
  );
}

export default KoontilaskuDialog;
